import { FirebaseApp, initializeApp } from 'firebase/app';
import { 
  Auth,
  getAuth
} from 'firebase/auth';
import { 
  Database,
  getDatabase, 
  ref, 
  set, 
  push, 
  onValue, 
  get, 
  remove,
  child,
  update
} from 'firebase/database';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  Firestore
} from 'firebase/firestore';
import {
  getPerformance,
  FirebasePerformance
} from 'firebase/performance';
import {
  FirebaseStorage,
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import {
  getFunctions,
  Functions
} from 'firebase/functions';
import {
  getRemoteConfig,
  RemoteConfig
} from 'firebase/remote-config';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL as string | undefined,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
};

// Weryfikacja czy zmienne środowiskowe zostały skonfigurowane (wyłączamy Firebase w testach E2E/Storybook)
const isFirebaseConfigured = !!(
  firebaseConfig.apiKey && 
  firebaseConfig.databaseURL && 
  firebaseConfig.projectId
) && !(typeof window !== 'undefined' && window.navigator.webdriver);

let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let db: Database | undefined;
let storage: FirebaseStorage | undefined;
let perf: FirebasePerformance | undefined;
let functions: Functions | undefined;
let remoteConfig: RemoteConfig | undefined;
let fs: Firestore | undefined;

if (isFirebaseConfigured) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getDatabase(app);
    const firestoreDbId = import.meta.env.VITE_FIREBASE_FIRESTORE_DATABASE_ID as string | undefined;
    const firestoreSettings = {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
      })
    };
    fs = firestoreDbId 
      ? initializeFirestore(app, firestoreSettings, firestoreDbId) 
      : initializeFirestore(app, firestoreSettings);
    storage = getStorage(app);
    perf = getPerformance(app);
    functions = getFunctions(app, 'europe-west1'); // Używamy europe-west1 dla Europy
    
    // Inicjalizacja Remote Config
    remoteConfig = getRemoteConfig(app);
    remoteConfig.settings.minimumFetchIntervalMillis = 60000; // 1 minuta dla szybkiego odświeżania
    remoteConfig.defaultConfig = {
      enable_payment: true,
      require_subscription: true,
      enable_new_pallete: false,
      support_email: 'kontakt@centrumcoucha.pl',
      login_url: '',
      client_password_min_length: 10,
      client_password_min_uppercase: 1,
      client_password_min_lowercase: 1,
      client_password_min_numbers: 1,
      client_password_min_special_chars: 1,
    };
    
    console.log("🔥 Połączono z Firebase pomyślnie. Inicjalizacja Performance Monitoring, Storage, Functions i Remote Config.");
  } catch (error) {
    console.error("Błąd podczas inicjalizacji Firebase:", error);
  }
} else {
  console.warn(
    "⚠️ Brak konfiguracji Firebase. Aplikacja działa w trybie DEMO (LocalStorage).\n" +
    "Skopiuj plik .env.example do .env i uzupełnij go swoimi kluczami Firebase, aby połączyć się z chmurą."
  );
}

export { 
  app,
  auth, 
  db, 
  perf,
  functions,
  remoteConfig,
  fs,
  isFirebaseConfigured,
  // Eksportujemy też przydatne funkcje Firebase Database dla łatwego dostępu
  ref,
  set,
  push,
  onValue,
  get,
  remove,
  child,
  update,
  // Firebase Storage
  storage,
  storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject
};
